import { supabase } from '@/lib/supabase';

export interface VehicleTypeRow {
  id: string;
  vehicle_type: string;
  icon_style: 'car' | 'truck';
  colour_hex: string | null;
  overlay_asset_key: string | null;
  slot_length_minutes: number;
  load_buffer_minutes: number;
  unload_buffer_minutes: number;
  return_buffer_required: boolean;
  return_buffer_trigger: 'service_area' | 'drive_time';
  return_buffer_minutes: number;
  drive_time_threshold_minutes: number;
  min_spacing_minutes: number;
  max_jobs_per_period: number | null;
  is_active: boolean;
  sort_order: number;
}

// Small in-memory cache: several small, frequently-rendered components (e.g.
// ShiftCard, rendered once per shift) each call loadVehicleTypes() rather
// than threading it down as a prop -- caching the in-flight/most recent
// result means that only costs one real network request, not one per
// component instance. Cleared automatically after 60s so edits in Settings
// show up without a full page reload.
let cachedActive: { at: number; promise: Promise<VehicleTypeRow[]> } | null = null;
const CACHE_MS = 60_000;

/** The single source of truth for vehicle types -- everything that used to hardcode its own Sedan/TMV/Van/SUV list reads from here instead. */
export const loadVehicleTypes = async (activeOnly = true): Promise<VehicleTypeRow[]> => {
  if (activeOnly) {
    if (!cachedActive || Date.now() - cachedActive.at > CACHE_MS) {
      cachedActive = { at: Date.now(), promise: fetchVehicleTypes(true) };
    }
    return cachedActive.promise;
  }
  return fetchVehicleTypes(false);
};

const fetchVehicleTypes = async (activeOnly: boolean): Promise<VehicleTypeRow[]> => {
  let query = supabase.from('vehicle_types').select('*').order('sort_order', { ascending: true });
  if (activeOnly) query = query.eq('is_active', true);
  const { data, error } = await query;
  if (error) {
    console.error('Error loading vehicle types:', error);
    return [];
  }
  return (data as VehicleTypeRow[]) || [];
};

export const getIconStyle = (
  vehicleType: string | null | undefined,
  types: VehicleTypeRow[]
): 'car' | 'truck' => {
  if (!vehicleType) return 'car';
  return types.find((t) => t.vehicle_type === vehicleType)?.icon_style || 'car';
};

export const getVehicleTypeColour = (
  vehicleType: string | null | undefined,
  types: VehicleTypeRow[]
): string | null => {
  if (!vehicleType) return null;
  return types.find((t) => t.vehicle_type === vehicleType)?.colour_hex ?? null;
};

export interface CompatibilityRow {
  vehicle_type: string;
  can_serve_job_type: string;
}

/**
 * Which vehicle types can cover a given job-required type (e.g. an SUV job
 * can be covered by a Car or an SUV). Configured in Settings -> Vehicle
 * Types; not yet wired into any job-matching logic since job records don't
 * carry a required-type field today -- ready for when that lands.
 */
export const loadVehicleTypeCompatibility = async (): Promise<CompatibilityRow[]> => {
  const { data, error } = await supabase
    .from('vehicle_type_compatibility')
    .select('vehicle_type, can_serve_job_type');
  if (error) {
    console.error('Error loading vehicle type compatibility:', error);
    return [];
  }
  return (data as CompatibilityRow[]) || [];
};

export const getCompatibleVehicleTypes = (
  jobRequiredType: string,
  matrix: CompatibilityRow[]
): string[] => {
  return matrix
    .filter((row) => row.can_serve_job_type === jobRequiredType)
    .map((row) => row.vehicle_type);
};

export interface SlotColourRow {
  vehicle_type: string;
  slot_number: number;
  booked_colour_hex: string;
  available_colour_hex: string;
}

/**
 * Booked/available colours per (vehicle type, slot number) for the Fleet
 * Capacity slot sheet -- configured in Settings -> Vehicle Types -> Slot
 * Colours. No caching (tiny table, and colours should reflect edits made in
 * Settings the moment the Fleet Capacity page is next loaded).
 */
export const loadSlotColours = async (): Promise<SlotColourRow[]> => {
  const { data, error } = await supabase
    .from('vehicle_type_slot_colours')
    .select('vehicle_type, slot_number, booked_colour_hex, available_colour_hex');
  if (error) {
    console.error('Error loading vehicle type slot colours:', error);
    return [];
  }
  return (data as SlotColourRow[]) || [];
};

export interface VehicleCapacityRow {
  vehicle_number: string;
  seat_capacity: number | null;
  wheelchair_capacity: number | null;
  luggage_capacity: number | null;
  wc_onboard: boolean;
  has_carseat: boolean;
  has_booster: boolean;
  has_paywave: boolean;
  is_tgb_vehicle: boolean;
  has_500kg_hoist: boolean;
}

/** Per-vehicle capacity + equipment, for the Fleet Capacity slot sheet's capacity columns. No caching -- small table. */
export const loadVehicleCapacity = async (): Promise<VehicleCapacityRow[]> => {
  const { data, error } = await supabase
    .from('vehicles')
    .select(
      'vehicle_number, seat_capacity, wheelchair_capacity, luggage_capacity, wc_onboard, has_carseat, has_booster, has_paywave, is_tgb_vehicle, has_500kg_hoist'
    );
  if (error) {
    console.error('Error loading vehicle capacity:', error);
    return [];
  }
  return (data as VehicleCapacityRow[]) || [];
};

export interface CapacityRequirement {
  passengers?: number;
  wheelchairs?: number;
  luggage?: number;
}

/**
 * Whether a vehicle's recorded capacity covers a job's requirement (e.g.
 * don't offer a 4-seat car for a 5-passenger booking). A vehicle with no
 * capacity recorded yet is treated as unknown, not disqualifying, so
 * unconfigured vehicles aren't silently hidden. Not wired into any live
 * booking flow yet -- TaxiCaller doesn't send a job's passenger/wheelchair/
 * luggage count into taxicaller_data today -- ready for when it does.
 */
export const meetsCapacity = (vehicle: VehicleCapacityRow, required: CapacityRequirement): boolean => {
  if (required.passengers != null && vehicle.seat_capacity != null && vehicle.seat_capacity < required.passengers) {
    return false;
  }
  if (
    required.wheelchairs != null &&
    vehicle.wheelchair_capacity != null &&
    vehicle.wheelchair_capacity < required.wheelchairs
  ) {
    return false;
  }
  if (required.luggage != null && vehicle.luggage_capacity != null && vehicle.luggage_capacity < required.luggage) {
    return false;
  }
  return true;
};
